import Title from '@cohort/components-xps/components/Title';
import type {RadioGroupTogglerOption} from '@cohort/wallet/components/RadioGroupToggler';
import RadioGroupToggler from '@cohort/wallet/components/RadioGroupToggler';
import {AboutBrand} from '@cohort/wallet/components/space/AboutBrand';
import PerksList from '@cohort/wallet/components/space/PerksList';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import SpacePage from '@cohort/wallet/layouts/space/SpacePage';
import {Fragment, useState} from 'react';
import {useTranslation} from 'react-i18next';

type PerkListFilters = 'available' | 'used';

const PerkListPage = (): JSX.Element => {
  const {space} = useSpaceContext();
  const {t} = useTranslation('pages', {keyPrefix: 'space.perkListPage'});
  const [filter, setFilter] = useState<PerkListFilters>('available');

  if (space === undefined) {
    return <Fragment />;
  }

  const options: RadioGroupTogglerOption[] = [
    {
      value: 'available',
      label: t('perkAvailable'),
      testId: 'available-perks-toggle-button',
    },
    {
      value: 'used',
      label: t('perkUsed'),
      testId: 'used-perks-toggle-button',
    },
  ];

  const availablePerks = space.perks.filter(
    p =>
      p.instances.filter(access => access.canBeUsed || access.raffleStatus === 'pending').length > 0
  );

  const usedPerks = space.perks.filter(p => !availablePerks.includes(p));

  return (
    <SpacePage
      title={t('title')}
      leftPanel={<AboutBrand />}
      tracking={{
        namespace: 'perks.list',
      }}
    >
      <div className="mb-4 flex flex-col items-center justify-between gap-4 md:flex-row">
        <Title content={t('title')} classes="text-2xl" />

        <div className="w-full lg:w-[400px]">
          <RadioGroupToggler
            options={options}
            onChange={option => setFilter(option.value as PerkListFilters)}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            currentOption={options.find(o => o.value === filter)!}
            tracking={{
              namespace: 'perks.list.filter',
              metadata: {
                perkStatus: filter,
              },
            }}
          />
        </div>
      </div>
      <PerksList perks={filter === 'available' ? availablePerks : usedPerks} displayMode="grid" />
    </SpacePage>
  );
};

export default PerkListPage;
